import { Stripe, StripeElements } from "@stripe/stripe-js";
import { ErrorResult, SuccessResult } from "./types";
import { useState } from "react";
import { ORDER_SUCCESS_BUYER } from "../../../../constants/routes";
import { usePaymentIntentCreate } from "../../../../hooks/api/v2/paymentIntent/usePaymentIntentCreate";
import { isHttpError } from "~/utils/httpClient";
import { mapToUserError } from "./mapToUserError";
import { useNavigate } from "react-router";
import { useCreditProcessorInitiatePayment } from "../../../../hooks/api/v2/creditProcessor/useCreditProcessorInitiatePayment";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { useRyftPayment } from "./useRyftPayment";

export type ProcessOrderReturnType =
  | SuccessResult<{ id: number }>
  | ErrorResult<Error>;

type BasePlaceOrderType = {
  supplierUrlIdInParam?: string;
  processOrder: () => Promise<ProcessOrderReturnType>;
  returnUrl?: string | ((_orderId: number) => string);
  onSuccess?: (_orderId: number) => void;
};

interface PlaceOrderWithNewCard extends BasePlaceOrderType {
  type: "place-order-with-new-card";
  stripePaymentMethodId: undefined;
  stripeElements: StripeElements;
  stripe: Stripe;
}

interface PlaceOrderWithExistingCard extends BasePlaceOrderType {
  type: "place-order-with-existing-card";
  stripePaymentMethodId: string;
  stripeElements: undefined;
  stripe: Stripe;
}

interface PlaceOrderWithoutCard extends BasePlaceOrderType {
  type: "place-order-without-card";
}

interface PlaceOrderWithSellarCredit extends BasePlaceOrderType {
  type: "place-order-with-sellar-credit";
}

interface PlaceOrderWithNewCardPaymentWithDeposit extends BasePlaceOrderType {
  type: "place-order-with-new-card-payment-with-deposit";
  stripePaymentMethodId: undefined;
  stripeElements: StripeElements;
  stripe: Stripe;
}

interface PlaceOrderWithExistingCardPaymentWithDeposit extends BasePlaceOrderType {
  type: "place-order-with-existing-card-payment-with-deposit";
  stripePaymentMethodId: string;
  stripeElements: undefined;
  stripe: Stripe;
}

interface PlaceOrderWithNewRyftCard extends BasePlaceOrderType {
  type: "place-order-with-new-ryft-card";
}

type PlaceOrderParams =
  | PlaceOrderWithNewCard
  | PlaceOrderWithExistingCard
  | PlaceOrderWithoutCard
  | PlaceOrderWithSellarCredit
  | PlaceOrderWithNewCardPaymentWithDeposit
  | PlaceOrderWithExistingCardPaymentWithDeposit
  | PlaceOrderWithNewRyftCard;

type DefaultMode = {
  type: "default";
};

export type PriceMismatchMode = {
  type: "price-mismatch";
  data: {
    clientPrice: number;
    serverPrice: number;
  };
};

/**
 * We can place orders while in the following modes
 * Default mode - This is the standard checkout which most users will go through
 * PriceMismatch mode - This mode is triggered when there has been a price change between the server and client
 * It is a temporary mode where upon acknowledging the price change, users fall back to the default mode
 */
export type PlaceOrderMode = DefaultMode | PriceMismatchMode;

export type PlaceOrderFailureStage = "order-creation" | "payment";

export const usePlaceOrder = ({
  onError: reportError,
}: {
  onError?: (_stage: PlaceOrderFailureStage, _error: unknown) => void;
} = {}) => {
  const ryftPayment = useRyftPayment();
  const stripeIntentMutation = usePaymentIntentCreate();
  const creditProcessorInitiatePaymentMutation =
    useCreditProcessorInitiatePayment();

  const navigate = useNavigate();
  const [placeOrderMode, setPlaceOrderMode] = useState<PlaceOrderMode>({
    type: "default",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [type, setType] = useState<PlaceOrderParams["type"] | null>(null);

  const navigateToSuccess = ({ url }: { url: string }) => {
    navigate(url, { replace: true });
  };

  const getStripePaymentIntent = async ({
    orderId,
    stripePaymentMethodId,
    orderPaymentType,
  }: {
    orderId: number;
    stripePaymentMethodId: string | undefined;
    orderPaymentType?: "cardDeposit";
  }) => {
    return stripeIntentMutation
      .mutateAsync({
        orderId,
        stripePaymentMethodId,
        orderPaymentType,
      })
      .then((data) => {
        return {
          data,
          error: null,
        };
      })
      .catch((error) => {
        const newError = isHttpError(error)
          ? error
          : new Error("Something went wrong");
        return { data: null, error: newError };
      });
  };

  const placeOrderWithCard = async ({
    stripePaymentMethodId,
    stripeElements,
    supplierUrlIdInParam,
    returnUrl,
    onSuccess,
    stripe,
    processOrder,
  }: PlaceOrderWithNewCard | PlaceOrderWithExistingCard) => {
    // validate the state of the Payment Element

    if (stripeElements) {
      const { error } = await stripeElements.submit();
      if (error) {
        setError(error.message ?? "");
        reportError?.("payment", error);
        return;
      }
    }
    // order creation/update

    const { data: order, error: processOrderError } = await processOrder();
    if (processOrderError) {
      handleUnableToProcessOrder(processOrderError);
      reportError?.("order-creation", processOrderError);
      return;
    }

    // create stripe payment intent

    const { data: stripeIntentResponse, error: stripeIntentResponseError } =
      await getStripePaymentIntent({
        orderId: order.id,
        stripePaymentMethodId,
      });

    if (stripeIntentResponseError) {
      handleUnableToProcessOrder(stripeIntentResponseError);
      reportError?.("payment", stripeIntentResponseError);
      return;
    }

    const orderSuccessUrl = supplierUrlIdInParam
      ? ORDER_SUCCESS_BUYER.href({
          supplierIdOrUrlId: supplierUrlIdInParam,
          orderId: order.id,
        })
      : "";
    const stripeReturnUrl =
      typeof returnUrl === "function"
        ? returnUrl(order.id)
        : (returnUrl ?? `${import.meta.env.VITE_URL}${orderSuccessUrl}`);

    await stripe
      .confirmPayment({
        elements: stripeElements,
        clientSecret: stripeIntentResponse.clientSecret,
        confirmParams: {
          return_url: stripeReturnUrl,
        },
      })
      .then((result) => {
        if (result.error) reportError?.("payment", result.error);
      })
      .finally(() => {
        if (onSuccess) {
          onSuccess(order.id);
        } else if (orderSuccessUrl) {
          navigateToSuccess({
            url: orderSuccessUrl,
          });
        }
      });
  };

  const placeOrderWithCardPaymentWithDeposit = async ({
    stripePaymentMethodId,
    stripeElements,
    supplierUrlIdInParam,
    returnUrl,
    onSuccess,
    stripe,
    processOrder,
  }:
    | PlaceOrderWithNewCardPaymentWithDeposit
    | PlaceOrderWithExistingCardPaymentWithDeposit) => {
    // validate the state of the Payment Element, also creates payment method

    const cardElement = stripeElements?.getElement("card");

    if (!cardElement && !stripePaymentMethodId) {
      const error = new Error("No card element or existing payment method");
      setError(error.message);
      reportError?.("payment", error);
      return;
    }

    // if new card is used, create payment method with card

    const createPaymentMethodResponse = cardElement
      ? await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        })
      : null;

    if (createPaymentMethodResponse?.error) {
      setError(createPaymentMethodResponse.error.message ?? "");
      reportError?.("payment", createPaymentMethodResponse.error);
      return;
    }

    // if using existing card, validate

    if (stripeElements) {
      const { error } = await stripeElements.submit();
      if (error) {
        setError(error.message ?? "");
        reportError?.("payment", error);
        return;
      }
    }

    // order creation/update

    const { data: order, error: processOrderError } = await processOrder();
    if (processOrderError) {
      handleUnableToProcessOrder(processOrderError);
      reportError?.("order-creation", processOrderError);
      return;
    }

    // create stripe payment intent with payment type 'cardDeposit'

    const { data: stripeIntentResponse, error: stripeIntentResponseError } =
      await getStripePaymentIntent({
        orderId: order.id,
        stripePaymentMethodId:
          stripePaymentMethodId ??
          createPaymentMethodResponse?.paymentMethod.id,
        orderPaymentType: "cardDeposit",
      });

    if (stripeIntentResponseError) {
      handleUnableToProcessOrder(stripeIntentResponseError);
      reportError?.("payment", stripeIntentResponseError);
      return;
    }

    const orderSuccessUrl = supplierUrlIdInParam
      ? ORDER_SUCCESS_BUYER.href({
          supplierIdOrUrlId: supplierUrlIdInParam,
          orderId: order.id,
        })
      : "";
    const stripeReturnUrl =
      typeof returnUrl === "function"
        ? returnUrl(order.id)
        : (returnUrl ?? `${import.meta.env.VITE_URL}${orderSuccessUrl}`);

    await stripe
      .confirmCardPayment(
        stripeIntentResponse.clientSecret,

        cardElement
          ? {
              payment_method: {
                card: cardElement,
              },
              return_url: stripeReturnUrl,
            }
          : {
              payment_method: stripePaymentMethodId,
              return_url: stripeReturnUrl,
            },
      )
      .then((result) => {
        if (result.error) reportError?.("payment", result.error);
      })
      .finally(() => {
        if (onSuccess) {
          onSuccess(order.id);
        } else if (orderSuccessUrl) {
          navigateToSuccess({
            url: orderSuccessUrl,
          });
        }
      });
  };

  const placeOrderWithoutCard = async ({
    processOrder,
    supplierUrlIdInParam,
    onSuccess,
  }: PlaceOrderWithoutCard) => {
    // order creation/update

    const { data: order, error: processOrderError } = await processOrder();
    if (processOrderError) {
      handleUnableToProcessOrder(processOrderError);
      reportError?.("order-creation", processOrderError);
      return;
    }
    if (onSuccess) {
      onSuccess(order.id);
      return;
    }
    const orderSuccessUrl = supplierUrlIdInParam
      ? ORDER_SUCCESS_BUYER.href({
          supplierIdOrUrlId: supplierUrlIdInParam,
          orderId: order.id,
        })
      : "";
    if (orderSuccessUrl) {
      navigateToSuccess({ url: orderSuccessUrl });
    }
  };

  const placeOrderWithSellarCredit = async ({
    supplierUrlIdInParam,
    processOrder,
    returnUrl,
    onSuccess,
  }: PlaceOrderWithSellarCredit) => {
    // order creation/update

    const { data: order, error: processOrderError } = await processOrder();

    if (processOrderError) {
      handleUnableToProcessOrder(processOrderError);
      reportError?.("order-creation", processOrderError);
      return;
    }

    const orderId = order.id;

    const orderSuccessUrl = supplierUrlIdInParam
      ? ORDER_SUCCESS_BUYER.href({
          supplierIdOrUrlId: supplierUrlIdInParam,
          orderId,
        })
      : "";

    const handleSuccess = () => {
      if (onSuccess) {
        onSuccess(orderId);
      } else if (orderSuccessUrl) {
        navigateToSuccess({ url: orderSuccessUrl });
      }
    };

    const creditReturnUrl =
      typeof returnUrl === "function" ? returnUrl(orderId) : returnUrl;

    await creditProcessorInitiatePaymentMutation.mutateAsync(
      {
        orderId,
        returnUrl: creditReturnUrl,
      },
      {
        onSuccess: (response) => {
          if (response.paymentUrl) {
            window.location.replace(response.paymentUrl);
            return;
          }
          handleSuccess();
        },
        onError: (error) => {
          if (!creditReturnUrl) {
            handleSuccess();
            return;
          }
          handleUnableToProcessOrder(error);
          reportError?.("payment", error);
        },
      },
    );
  };

  const placeOrderWithNewRyftCard = async ({
    supplierUrlIdInParam,
    processOrder,
    onSuccess,
  }: PlaceOrderWithNewRyftCard) => {
    const ryftValidationErrors =
      ryftPayment.cardFormValidationEvent?.validationErrors ?? [];

    // Validate the state of the Ryft card payment element
    if (ryftValidationErrors.length > 0) {
      setError(ryftValidationErrors[0]?.message ?? "");
      reportError?.("payment", ryftValidationErrors);
      return;
    }

    if (!ryftPayment.paymentSessionId) {
      setError("Payment session not found");
      reportError?.("payment", "Payment session not found");
      return;
    }

    if (!ryftPayment.cardFormInstanceRef.current) {
      const message =
        "The card form isn't ready yet. Please wait a moment and try again.";
      setError(message);
      reportError?.("payment", message);
      return;
    }

    const { data: order, error: processOrderError } = await processOrder();

    if (processOrderError) {
      handleUnableToProcessOrder(processOrderError);
      reportError?.("order-creation", processOrderError);
      return;
    }

    const updateRyftPaymentSessionResponse =
      await ryftPayment.updatePaymentSession({
        paymentSessionId: ryftPayment.paymentSessionId,
        orderId: order.id,
      });

    const orderSuccessUrl = supplierUrlIdInParam
      ? ORDER_SUCCESS_BUYER.href({
          supplierIdOrUrlId: supplierUrlIdInParam,
          orderId: order.id,
        })
      : "";

    const handleSuccess = () => {
      if (onSuccess) {
        onSuccess(order.id);
      } else if (orderSuccessUrl) {
        navigateToSuccess({ url: orderSuccessUrl });
      }
    };

    // Errors from updating the Ryft payment session should still send the user to the success page
    if (updateRyftPaymentSessionResponse.error) {
      handleSuccess();
      return;
    }

    const payWithRyftResponse = await ryftPayment.payWithRyft();

    // Errors from attempting to pay with Ryft should still send the user to the success page
    if (payWithRyftResponse.error) {
      handleSuccess();
      return;
    }

    handleSuccess();
  };

  /**
   * Handles errors associated with placing an order
   * For example - Price mismatch errors
   */
  const handleUnableToProcessOrder = (error: unknown) => {
    const errorDetails = apiV2AssertError(error)?.response?.data.details;

    if (errorDetails?.code === "PRICE_MISMATCH") {
      setPlaceOrderMode({
        type: "price-mismatch",
        data: errorDetails.data,
      });
    }
    setError(mapToUserError(error));
  };

  const onComplete = () => {
    setLoading(false);
    setType(null);
  };

  const placeOrder = async (params: PlaceOrderParams) => {
    setError("");
    setLoading(true);
    setType(params.type);
    setPlaceOrderMode({
      type: "default",
    });
    switch (params.type) {
      case "place-order-with-new-card": {
        placeOrderWithCard(params).finally(onComplete);
        return;
      }

      case "place-order-with-existing-card": {
        placeOrderWithCard(params).finally(onComplete);
        return;
      }

      case "place-order-without-card": {
        placeOrderWithoutCard(params).finally(onComplete);
        return;
      }

      case "place-order-with-sellar-credit": {
        placeOrderWithSellarCredit(params).finally(onComplete);
        return;
      }
      case "place-order-with-new-card-payment-with-deposit": {
        placeOrderWithCardPaymentWithDeposit(params).finally(onComplete);
        return;
      }
      case "place-order-with-existing-card-payment-with-deposit": {
        placeOrderWithCardPaymentWithDeposit(params).finally(onComplete);
        return;
      }

      case "place-order-with-new-ryft-card": {
        placeOrderWithNewRyftCard(params).finally(onComplete);
        return;
      }

      default: {
        const error = new Error("An error occurred while placing this order");
        setError(error.message);
        reportError?.("payment", error);
        return;
      }
    }
  };

  return {
    loading,
    error,
    type,
    mode: placeOrderMode,
    placeOrder,
    setError,
  };
};
